// frontend/src/marketing/HeroGraphic.jsx
import React from "react";
import PropTypes from "prop-types";

/**
 * HeroGraphic — decorative, self-contained SVG combining a stylised QR code with
 * a small analytics card, illustrating "scan → real-time analytics". Purely
 * presentational; marked aria-hidden. No external assets, so no layout shift.
 */
export default function HeroGraphic({ className }) {
  // A deterministic, hand-picked QR-ish module pattern (7x7 inner grid).
  const modules = [
    [1, 1, 0, 1, 0, 1, 1],
    [1, 0, 0, 0, 1, 0, 1],
    [0, 1, 1, 1, 0, 1, 0],
    [1, 0, 1, 0, 1, 0, 1],
    [0, 1, 0, 1, 1, 1, 0],
    [1, 0, 1, 0, 0, 0, 1],
    [1, 1, 0, 1, 0, 1, 1],
  ];
  const cell = 14;
  const gridOrigin = 34;

  return (
    <svg
      viewBox="0 0 420 360"
      className={className}
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="qf-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#eff6ff" />
          <stop offset="1" stopColor="#dbeafe" />
        </linearGradient>
        <linearGradient id="qf-bar" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor="#3b82f6" />
          <stop offset="1" stopColor="#60a5fa" />
        </linearGradient>
      </defs>

      {/* Backdrop */}
      <rect x="0" y="0" width="420" height="360" rx="24" fill="url(#qf-bg)" />

      {/* QR card */}
      <g transform="translate(28 40)">
        <rect width="180" height="180" rx="18" fill="#ffffff" />
        <rect width="180" height="180" rx="18" fill="none" stroke="#bfdbfe" />
        {/* finder squares */}
        {[
          [16, 16],
          [124, 16],
          [16, 124],
        ].map(([x, y]) => (
          <g key={`${x}-${y}`} transform={`translate(${x} ${y})`}>
            <rect width="40" height="40" rx="8" fill="#1e3a8a" />
            <rect x="8" y="8" width="24" height="24" rx="4" fill="#ffffff" />
            <rect x="14" y="14" width="12" height="12" rx="2" fill="#1e3a8a" />
          </g>
        ))}
        {/* data modules */}
        {modules.flatMap((row, r) =>
          row.map((on, c) =>
            on ? (
              <rect
                key={`${r}-${c}`}
                x={gridOrigin + c * cell}
                y={gridOrigin + r * cell}
                width={cell - 3}
                height={cell - 3}
                rx="2"
                fill="#2563eb"
              />
            ) : null,
          ),
        )}
      </g>

      {/* Analytics card */}
      <g transform="translate(214 96)">
        <rect width="184" height="184" rx="18" fill="#ffffff" />
        <rect width="184" height="184" rx="18" fill="none" stroke="#bfdbfe" />
        <text x="20" y="34" fontFamily="Inter, sans-serif" fontSize="13" fontWeight="600" fill="#111827">
          Scans today
        </text>
        <text x="20" y="62" fontFamily="Inter, sans-serif" fontSize="26" fontWeight="700" fill="#1e3a8a">
          12,480
        </text>
        {/* bars */}
        {[40, 70, 52, 96, 64, 120, 88].map((h, i) => (
          <rect
            key={i}
            x={20 + i * 22}
            y={150 - h}
            width="14"
            height={h}
            rx="3"
            fill="url(#qf-bar)"
          />
        ))}
        <line x1="16" y1="152" x2="168" y2="152" stroke="#e5e7eb" />
      </g>
    </svg>
  );
}

HeroGraphic.propTypes = { className: PropTypes.string };
HeroGraphic.defaultProps = { className: "w-full h-auto" };
